/**
 * @file: MessageListFlashList.tsx
 *
 * The inverted message list backed by `@shopify/flash-list` — a recycling
 * list that decodes/mounts far fewer rows than `FlatList`, so an image-heavy
 * history scrolls smoothly. Drop-in for `MessageListFlatList` via the shared
 * `MessageListProps`; selected in `MessageList.tsx`.
 *
 * FlashList differences handled here:
 * - No `style` prop — the wrapper View sizes it.
 * - No `onScrollToIndexFailed` — FlashList resolves a failed scroll itself.
 * - Short-history top-align: FlashList ignores `flexGrow`/`justifyContent` in
 *   the public `contentContainerStyle` (padding only), so a short history would
 *   pin to the visual bottom. `overrideProps` injects those onto the underlying
 *   scroll content container, where they DO apply — the FlatList trick, no
 *   measuring, no jank. Crucially the PADDING stays on the public
 *   `contentContainerStyle`: FlashList derives the cell-width inset from it, so
 *   moving padding into `overrideProps` (as a first attempt did) drops the
 *   inset and the bubbles overflow the edge.
 */

import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { ChatMsg, keyOfChatMsg } from '@euler/model/chat';
import { useBehaviorSubject } from '@euler/utils/hooks';
import {
  FlashList,
  ListRenderItem as FlashListRenderItem,
} from '@shopify/flash-list';
import { FC, RefObject, memo, useMemo } from 'react';
import { View } from 'react-native';
import {
  LoadingOlderFooter,
  MESSAGE_LIST_BG,
  MessageListProps,
} from './messageListShared';

export const MessageListFlashList: FC<MessageListProps> = memo(
  ({
    messages,
    renderItem,
    flatListRef,
    onScroll,
    onEndReached,
    loadingOlder$,
    style,
  }) => {
    const { rpx } = useResponsiveContext();
    const [loadingOlder] = useBehaviorSubject(loadingOlder$);

    // Top-align a short history (inverted): apply the FlatList trick to the
    // underlying scroll content container via `overrideProps` — flexGrow fills
    // the viewport only when the content is shorter than it, so long histories
    // scroll normally. Padding is repeated here because `overrideProps`
    // REPLACES the scroll container's style: the public `contentContainerStyle`
    // below still drives the cell-WIDTH inset, but the left/right cell POSITION
    // comes from this padding — omit it and the left gap collapses while the
    // inset piles onto the right (asymmetric margins).
    const overrideProps = useMemo(
      () => ({
        contentContainerStyle: {
          flexGrow: 1,
          justifyContent: 'flex-end',
          padding: rpx(12),
        },
      }),
      [rpx],
    );

    return (
      <View style={[{ flex: 1, backgroundColor: MESSAGE_LIST_BG }, style]}>
        <FlashList
          ref={flatListRef as unknown as RefObject<FlashList<ChatMsg>>}
          data={messages}
          inverted
          keyExtractor={keyOfChatMsg}
          renderItem={renderItem as unknown as FlashListRenderItem<ChatMsg>}
          // Rough average bubble height; FlashList uses it only to size its
          // initial render window, then measures for real.
          estimatedItemSize={Math.round(rpx(120))}
          contentContainerStyle={{ padding: rpx(12) }}
          overrideProps={overrideProps}
          onScroll={onScroll}
          onEndReached={onEndReached}
          onEndReachedThreshold={0.3}
          ListFooterComponent={loadingOlder ? <LoadingOlderFooter /> : null}
          scrollEventThrottle={100}
        />
      </View>
    );
  },
);
